import { Link } from "@tanstack/react-router";
import { Plus, Receipt } from "@/components/icons";
import { Button, Skeleton } from "@/components/kit";
import { EXPENSE_CATEGORY_LABELS } from "@/modules/expenses/presentation/lib/category-labels";
import type { ProfitSummaryDTO } from "@/modules/reports/domain/types";
import { formatLak } from "@/shared/lib/currency";
import { ReportsQueryError } from "./ReportsQueryError";

type ProfitExpenseBreakdownProps = {
  summary?: ProfitSummaryDTO;
  isLoading?: boolean;
  isError?: boolean;
  onRetry?: () => void;
  isRetrying?: boolean;
};

export function ProfitExpenseBreakdown({
  summary,
  isLoading,
  isError,
  onRetry,
  isRetrying,
}: ProfitExpenseBreakdownProps) {
  if (isError && onRetry) {
    return (
      <section className="brand-card p-4 sm:p-5">
        <ReportsQueryError onRetry={onRetry} isRetrying={isRetrying} />
      </section>
    );
  }

  if (isLoading) {
    return <Skeleton className="h-56 w-full rounded-2xl" />;
  }

  const rows = summary?.expenseByCategory ?? [];
  const total = Number(summary?.totalExpenses ?? 0);
  const marketingPercent = summary?.marketingPercentOfRevenue ?? "0";

  return (
    <section className="brand-card flex flex-col gap-4 p-4 sm:p-5">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
        <div>
          <h2 className="font-display text-lg">ລາຍຈ່າຍຕາມປະເພດ</h2>
          <p className="text-muted-foreground text-sm">
            ລວມຕາມວັນທີ່ໃຊ້ຈ່າຍ ແລະ ໃຊ້ຫັກອອກຈາກ gross profit
          </p>
        </div>
        <Button asChild variant="outline" size="sm" className="rounded-full">
          <Link to="/app/expenses/new">
            <Plus data-icon="inline-start" />
            ເພີ່ມລາຍຈ່າຍ
          </Link>
        </Button>
      </div>

      {rows.length === 0 || total <= 0 ? (
        <div className="flex flex-col items-center justify-center gap-2 rounded-xl border border-secondary/25 border-dashed bg-muted/20 px-4 py-8 text-center">
          <Receipt className="text-secondary" />
          <p className="font-medium text-sm">ຍັງບໍ່ມີລາຍຈ່າຍໃນຊ່ວງນີ້</p>
          <p className="max-w-sm text-muted-foreground text-xs">
            ເພີ່ມຄ່າແອດ Facebook ຫຼື ລາຍຈ່າຍຮ້ານເພື່ອໃຫ້ net profit ສະທ້ອນຕົ້ນທຶນຈິງ
          </p>
        </div>
      ) : (
        <div className="flex flex-col gap-3">
          {rows.map((row) => {
            const amount = Number(row.amount);
            const percent = total > 0 ? (amount / total) * 100 : 0;
            return (
              <div key={row.category} className="flex flex-col gap-1.5">
                <div className="flex items-center justify-between gap-3 text-sm">
                  <span className="font-medium">
                    {EXPENSE_CATEGORY_LABELS[row.category]}
                  </span>
                  <span className="font-display text-secondary text-xl tabular-nums">
                    {formatLak(row.amount)}
                  </span>
                </div>
                <div className="h-2 overflow-hidden rounded-full bg-muted">
                  <div
                    className="h-full rounded-full bg-secondary"
                    style={{ width: `${Math.min(100, percent)}%` }}
                  />
                </div>
                <span className="text-muted-foreground text-xs">
                  {percent.toFixed(1)}% ຂອງລາຍຈ່າຍທັງໝົດ
                </span>
              </div>
            );
          })}
        </div>
      )}

      <div className="grid gap-3 border-secondary/15 border-t pt-3 text-sm sm:grid-cols-3">
        <div>
          <p className="text-muted-foreground text-xs">ລາຍຈ່າຍທັງໝົດ</p>
          <p className="font-display text-2xl text-foreground tabular-nums">
            {formatLak(summary?.totalExpenses ?? "0")}
          </p>
        </div>
        <div>
          <p className="text-muted-foreground text-xs">ຄ່າແອດ</p>
          <p className="font-display text-2xl text-secondary tabular-nums">
            {formatLak(summary?.marketingExpenses ?? "0")}
          </p>
        </div>
        <div>
          <p className="text-muted-foreground text-xs">ຄ່າແອດ / ລາຍຮັບ</p>
          <p className="font-display text-2xl text-foreground tabular-nums">
            {marketingPercent}%
          </p>
        </div>
      </div>
    </section>
  );
}
